import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { shopService } from '@/services/shopService'
import type {
  Shop,
  ShopListItem,
  ShopStatus,
  ShopStatistics,
  CreateShopRequest,
  UpdateShopRequest,
  StripeAccountStatus
} from '@/types/shop'
import { isSessionExpiredError } from '@/services/api'

const ACTIVE_SHOP_KEY = 'zaveato_active_shop_id'

export const useShopStore = defineStore('shop', () => {
  // ============================================================================
  // State
  // ============================================================================

  // The provider's shops (0, 1 or 2 — max 1 retail + 1 wholesale).
  // `myShop` below is a computed compat shim: it resolves to whichever shop
  // is currently active, so legacy views that read `shopStore.myShop.id`
  // continue to work while we migrate them.
  const myShops = ref<Shop[]>([])
  const activeShopId = ref<string | null>(null)

  const currentShop = ref<Shop | null>(null)
  const shops = ref<ShopListItem[]>([])
  const shopStatistics = ref<ShopStatistics | null>(null)
  const stripeAccountStatus = ref<StripeAccountStatus | null>(null)

  // Pagination state (for admin)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const totalCount = ref(0)
  const totalPages = ref(0)
  const searchTerm = ref('')
  const statusFilter = ref<ShopStatus | undefined>(undefined)
  const sortBy = ref('CreatedAt')
  const sortDirection = ref<'asc' | 'desc'>('desc')

  // Loading states
  const loading = ref(false)
  const loadingMyShop = ref(false)
  const loadingStatistics = ref(false)
  const loadingStripeStatus = ref(false)
  const saving = ref(false)

  // ============================================================================
  // Computed
  // ============================================================================

  // The active shop (whichever the user has selected in the switcher).
  // Falls back to the first shop if the persisted id is stale/unknown.
  const activeShop = computed<Shop | null>(() => {
    if (myShops.value.length === 0) return null
    if (activeShopId.value) {
      const match = myShops.value.find(s => s.id === activeShopId.value)
      if (match) return match
    }
    return myShops.value[0]
  })

  // Compat shim — legacy code reads shopStore.myShop.*; this keeps that path
  // working without touching every view. Views migrate to activeShop over time.
  const myShop = computed<Shop | null>(() => activeShop.value)

  const hasShop = computed(() => myShops.value.length > 0)
  const hasRetailShop = computed(() => myShops.value.some(s => !s.isWholesaler))
  const hasWholesaleShop = computed(() => myShops.value.some(s => s.isWholesaler))
  // Direct handles to each shop type — use these anywhere a workflow is
  // type-specific (wholesale invoicing, procurement catalogs). Prevents the
  // "user has a wholesale shop but active is retail → wholesale UI hides"
  // class of bug.
  const retailShop = computed<Shop | null>(() => myShops.value.find(s => !s.isWholesaler) ?? null)
  const wholesaleShop = computed<Shop | null>(() => myShops.value.find(s => s.isWholesaler) ?? null)
  // Which shop type is missing (drives the "+ Add {type} Shop" button). Null
  // when both exist — no add button shown.
  const missingShopType = computed<'retail' | 'wholesale' | null>(() => {
    if (myShops.value.length === 0) return 'retail'
    if (!hasRetailShop.value) return 'retail'
    if (!hasWholesaleShop.value) return 'wholesale'
    return null
  })

  // ============================================================================
  // Shop-scoped selectors — SINGLE source of truth for regionCode / currency /
  // timezone reads. Any view that needs the active shop's country/currency/tz
  // MUST consume these instead of re-implementing the `?? 'GBP'` fallback.
  // Mirrors the mobile pattern (`selectShopRegionCode`, `selectShopCurrency`,
  // `selectShopTimezone` in `mobile/src/store/shopStore.ts`).
  // ============================================================================

  /**
   * ISO 3166-1 alpha-2 region code of the active shop (`GB` / `PK` / `AE` /
   * `SA`). Uppercased, defaults to `GB` when unknown. Feed into helpers like
   * `getAddressFormat(rc)` or `getTimeZoneIdForRegion(rc)`.
   */
  const selectShopRegionCode = computed<string>(() => {
    const rc = (activeShop.value?.regionCode ?? '').trim().toUpperCase()
    return rc || 'GB'
  })

  /**
   * ISO 4217 currency code (`GBP` / `PKR` / `AED` / `SAR`) for the active
   * shop. Uppercased, defaults to `GBP`. Consumers should thread this into
   * `formatMoney(amount, currency)` — never symbol lookups.
   */
  const selectShopCurrency = computed<string>(() => {
    const cur = (activeShop.value?.currency ?? '').trim().toUpperCase()
    return cur || 'GBP'
  })

  /**
   * IANA timezone id for the active shop (e.g. "Europe/London",
   * "Asia/Karachi"). Defaults to `Europe/London`. Feed into
   * `useFormatDate().formatDateTime(iso, tz)` so wall-clock reads match the
   * operator's dashboard.
   */
  const selectShopTimezone = computed<string>(() => {
    const tz = (activeShop.value?.timezone ?? '').trim()
    return tz || 'Europe/London'
  })

  const isShopActive = computed(() => activeShop.value?.status === 'Active')

  const canAcceptOrders = computed(() => {
    return isShopActive.value &&
           stripeAccountStatus.value?.canAcceptOrders === true
  })

  const needsStripeSetup = computed(() => {
    if (activeShop.value?.status === 'Active') {
      return false
    }
    return !stripeAccountStatus.value?.hasAccount ||
           !stripeAccountStatus.value?.isComplete
  })

  // ============================================================================
  // Actions
  // ============================================================================

  /**
   * Fetch all shops (0, 1 or 2) owned by the current provider. Hydrates the
   * active-shop pointer from localStorage on first load, falling back to the
   * first shop when the persisted id is stale or unknown (e.g. shop deleted
   * on another device).
   */
  async function fetchMyShops() {
    loadingMyShop.value = true
    try {
      const shops = await shopService.getMyShops()
      myShops.value = shops

      // Hydrate active shop id — only trust localStorage if it points to a
      // shop we actually own. Prevents "wrong shop after account swap".
      const persisted = typeof localStorage !== 'undefined'
        ? localStorage.getItem(ACTIVE_SHOP_KEY)
        : null
      if (persisted && shops.some(s => s.id === persisted)) {
        activeShopId.value = persisted
      } else if (shops.length > 0) {
        activeShopId.value = shops[0].id
        persistActiveShopId(shops[0].id)
      } else {
        activeShopId.value = null
        if (typeof localStorage !== 'undefined') localStorage.removeItem(ACTIVE_SHOP_KEY)
      }
      return shops
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching my shops:', error)
      }
      myShops.value = []
      activeShopId.value = null
      throw error
    } finally {
      loadingMyShop.value = false
    }
  }

  /**
   * Compat wrapper — old callers used fetchMyShop() (singular). Delegates to
   * the plural version and returns the active shop.
   */
  async function fetchMyShop() {
    await fetchMyShops()
    return activeShop.value
  }

  function persistActiveShopId(id: string | null) {
    if (typeof localStorage === 'undefined') return
    if (id) localStorage.setItem(ACTIVE_SHOP_KEY, id)
    else localStorage.removeItem(ACTIVE_SHOP_KEY)
  }

  /**
   * Switch which shop is active. Persists to localStorage. Views should be
   * remounted after switching (e.g. via router.replace() + :key on router-view)
   * so shop-scoped queries re-run against the new active shop's id.
   */
  function switchShop(shopId: string) {
    if (!myShops.value.some(s => s.id === shopId)) {
      console.warn(`switchShop: unknown shop id ${shopId}`)
      return
    }
    activeShopId.value = shopId
    persistActiveShopId(shopId)
    // Clear per-shop caches — Stripe status is shop-scoped
    stripeAccountStatus.value = null
    shopStatistics.value = null
  }

  /**
   * Fetch shop by ID
   */
  async function fetchShop(id: string) {
    loading.value = true
    try {
      const shop = await shopService.getShop(id)
      currentShop.value = shop
      return shop
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching shop:', error)
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * Create a new shop. Adds to myShops and makes the newly-created shop active.
   */
  async function createShop(data: CreateShopRequest) {
    saving.value = true
    try {
      const shop = await shopService.createShop(data)
      // Replace an existing shop of the same type if present (defensive — BE
      // enforces uniqueness), else append.
      const idx = myShops.value.findIndex(s => s.id === shop.id)
      if (idx >= 0) myShops.value.splice(idx, 1, shop)
      else myShops.value.push(shop)
      activeShopId.value = shop.id
      persistActiveShopId(shop.id)
      return shop
    } catch (error) {
      console.error('Error creating shop:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  function upsertMyShop(shop: Shop) {
    const idx = myShops.value.findIndex(s => s.id === shop.id)
    if (idx >= 0) myShops.value.splice(idx, 1, shop)
  }

  /**
   * Clone an existing shop into a new sibling. Adds the clone to myShops and
   * makes it active so the user lands on its dashboard for review.
   */
  async function cloneShop(data: {
    sourceShopId: string
    name: string
    shopCode?: string
    isWholesaler: boolean
  }) {
    saving.value = true
    try {
      const shop = await shopService.cloneShop(data)
      myShops.value.push(shop)
      activeShopId.value = shop.id
      persistActiveShopId(shop.id)
      return shop
    } catch (error) {
      console.error('Error cloning shop:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Update shop
   */
  async function updateShop(id: string, data: UpdateShopRequest) {
    saving.value = true
    try {
      const shop = await shopService.updateShop(id, data)
      upsertMyShop(shop)
      if (currentShop.value?.id === id) {
        currentShop.value = shop
      }
      return shop
    } catch (error) {
      console.error('Error updating shop:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Activate shop
   */
  async function activateShop(id: string) {
    saving.value = true
    try {
      const shop = await shopService.activateShop(id)
      upsertMyShop(shop)
      if (currentShop.value?.id === id) {
        currentShop.value = shop
      }
      return shop
    } catch (error) {
      console.error('Error activating shop:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Deactivate shop
   */
  async function deactivateShop(id: string) {
    saving.value = true
    try {
      const shop = await shopService.deactivateShop(id)
      upsertMyShop(shop)
      if (currentShop.value?.id === id) {
        currentShop.value = shop
      }
      return shop
    } catch (error) {
      console.error('Error deactivating shop:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Fetch shop statistics
   */
  async function fetchShopStatistics(id: string, fromDate?: string, toDate?: string) {
    loadingStatistics.value = true
    try {
      const stats = await shopService.getShopStatistics(id, fromDate, toDate)
      shopStatistics.value = stats
      return stats
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching shop statistics:', error)
      }
      throw error
    } finally {
      loadingStatistics.value = false
    }
  }

  /**
   * Check shop code availability
   */
  async function checkShopCodeAvailability(shopCode: string) {
    try {
      return await shopService.checkShopCodeAvailability(shopCode)
    } catch (error) {
      console.error('Error checking shop code:', error)
      throw error
    }
  }

  // ============================================================================
  // Admin Actions
  // ============================================================================

  /**
   * Fetch all shops (admin only)
   */
  async function fetchAllShops() {
    loading.value = true
    try {
      const result = await shopService.getAllShops({
        search: searchTerm.value || undefined,
        status: statusFilter.value,
        page: currentPage.value,
        pageSize: pageSize.value,
        sortBy: sortBy.value,
        sortDirection: sortDirection.value
      })
      shops.value = result.items
      totalCount.value = result.totalCount
      totalPages.value = result.totalPages
      currentPage.value = result.page
    } catch (error) {
      if (!isSessionExpiredError(error)) {
        console.error('Error fetching shops:', error)
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  // ============================================================================
  // Stripe Actions
  // ============================================================================

  /**
   * Fetch Stripe account status
   */
  async function fetchStripeAccountStatus() {
    loadingStripeStatus.value = true
    try {
      const status = await shopService.getStripeAccountStatus()
      stripeAccountStatus.value = status
      return status
    } catch (error) {
      // Stripe account-status endpoint may not exist (Zaveato-managed payments)
      // Default to ready state — platform manages Stripe directly
      stripeAccountStatus.value = {
        hasAccount: true,
        isComplete: true,
        canAcceptOrders: true
      } as any
      return stripeAccountStatus.value
    } finally {
      loadingStripeStatus.value = false
    }
  }

  /**
   * Create Stripe Connect account
   */
  async function createStripeConnectAccount() {
    saving.value = true
    try {
      const result = await shopService.createStripeConnectAccount()
      // Refresh status after creation
      await fetchStripeAccountStatus()
      return result
    } catch (error) {
      console.error('Error creating Stripe account:', error)
      throw error
    } finally {
      saving.value = false
    }
  }

  /**
   * Get Stripe onboarding link
   */
  async function getStripeOnboardingLink(returnUrl: string, refreshUrl: string) {
    try {
      return await shopService.getStripeOnboardingLink(returnUrl, refreshUrl)
    } catch (error) {
      console.error('Error getting onboarding link:', error)
      throw error
    }
  }

  // ============================================================================
  // Pagination & Filtering
  // ============================================================================

  function setPage(page: number) {
    currentPage.value = page
    fetchAllShops()
  }

  function setPageSize(size: number) {
    pageSize.value = size
    currentPage.value = 1
    fetchAllShops()
  }

  function setSearchTerm(term: string) {
    searchTerm.value = term
    currentPage.value = 1
    fetchAllShops()
  }

  function setStatusFilter(status: ShopStatus | undefined) {
    statusFilter.value = status
    currentPage.value = 1
    fetchAllShops()
  }

  function setSorting(field: string, direction: 'asc' | 'desc' = 'desc') {
    sortBy.value = field
    sortDirection.value = direction
    fetchAllShops()
  }

  function clearFilters() {
    searchTerm.value = ''
    statusFilter.value = undefined
    sortBy.value = 'CreatedAt'
    sortDirection.value = 'desc'
    currentPage.value = 1
    fetchAllShops()
  }

  // ============================================================================
  // Reset
  // ============================================================================

  function resetStore() {
    myShops.value = []
    activeShopId.value = null
    if (typeof localStorage !== 'undefined') localStorage.removeItem(ACTIVE_SHOP_KEY)
    currentShop.value = null
    shops.value = []
    shopStatistics.value = null
    stripeAccountStatus.value = null
    currentPage.value = 1
    totalCount.value = 0
    totalPages.value = 0
    searchTerm.value = ''
    statusFilter.value = undefined
  }

  return {
    // State
    myShops,
    activeShopId,
    myShop,          // compat: computed → activeShop
    activeShop,
    currentShop,
    shops,
    shopStatistics,
    stripeAccountStatus,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    searchTerm,
    statusFilter,
    sortBy,
    sortDirection,
    loading,
    loadingMyShop,
    loadingStatistics,
    loadingStripeStatus,
    saving,

    // Computed
    hasShop,
    hasRetailShop,
    hasWholesaleShop,
    retailShop,
    wholesaleShop,
    missingShopType,
    isShopActive,
    canAcceptOrders,
    needsStripeSetup,
    selectShopRegionCode,
    selectShopCurrency,
    selectShopTimezone,

    // Actions
    fetchMyShop,      // compat wrapper
    fetchMyShops,
    switchShop,
    fetchShop,
    createShop,
    cloneShop,
    updateShop,
    activateShop,
    deactivateShop,
    fetchShopStatistics,
    checkShopCodeAvailability,

    // Admin Actions
    fetchAllShops,

    // Stripe Actions
    fetchStripeAccountStatus,
    createStripeConnectAccount,
    getStripeOnboardingLink,

    // Pagination & Filtering
    setPage,
    setPageSize,
    setSearchTerm,
    setStatusFilter,
    setSorting,
    clearFilters,

    // Reset
    resetStore
  }
})
